<template>
  <div class="mrl-matrix-container pa-2">
    <!-- Panel de Filtros -->
    <v-card class="mb-4 rounded-xl elevation-2 filter-panel" outlined>
      <v-card-text class="py-3">
        <!-- Filtros Principales -->
        <v-row dense align="end">
          <!-- Filtro Campaña -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <s-select-campaign-area
              label="Campaña"
              v-model="filters.cmaId"
              typeArea="1"
              clearable
              noDefault
            />
          </v-col>

          <!-- Filtro Tipo Cultivo -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <s-select-definition
              label="Tipo Cultivo"
              :def="1173"
              v-model="filters.typeCultive"
              clearable
            />
          </v-col>

          <!-- Filtro Tipo de Producto -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <s-select-definition
              label="Tipo de Producto"
              :def="1740"
              v-model="filters.TypeSubstances"
              clearable
            />
          </v-col>

          <!-- Filtro Sustancia -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <s-select-substances
              label="Sustancia"
              :type-substances="filters.TypeSubstances"
              v-model="filters.asuId"
              clearable
              noDefault
            />
          </v-col>

          <!-- Filtro Mercado / Destino -->
          <v-col cols="12" sm="6" md="3" lg="2">
            <s-select-definition
              label="Mercado / Destino"
              :def="1736"
              v-model="filters.typeMarket"
              clearable
            />
          </v-col>

          <!-- Botones de Acción -->
          <v-col cols="12" sm="6" md="3" lg="2" class="d-flex justify-end align-center pb-1">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  outlined
                  height="40"
                  class="mr-2 px-3 font-weight-bold rounded-lg"
                  :loading="loading"
                  @click="loadData"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Consultar
                </v-btn>
              </template>
              <span>Recargar matriz de LMR</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="success"
                  class="white--text px-3 font-weight-bold rounded-lg"
                  height="40"
                  elevation="1"
                  :disabled="matrixRows.length === 0"
                  @click="exportToExcel"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left>mdi-file-excel</v-icon>
                  Excel
                </v-btn>
              </template>
              <span>Exportar matriz a Excel (.xlsx)</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Resumen de KPIs -->
    <v-row dense class="mb-3">
      <v-col cols="12" sm="6">
        <v-card class="kpi-card gradient-blue rounded-xl" dark elevation="2">
          <v-card-text class="d-flex align-center py-2">
            <v-avatar color="rgba(255,255,255,0.2)" size="46" class="mr-3">
              <v-icon size="26">mdi-flask-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold opacity-80">
                {{ recordType === 2 ? 'Metales Pesados' : 'Sustancias Activas' }}
              </div>
              <div class="text-h5 font-weight-black">{{ filteredRows.length }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6">
        <v-card class="kpi-card gradient-teal rounded-xl" dark elevation="2">
          <v-card-text class="d-flex align-center py-2">
            <v-avatar color="rgba(255,255,255,0.2)" size="46" class="mr-3">
              <v-icon size="26">mdi-earth</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption text-uppercase font-weight-bold opacity-80">Mercados / Destinos</div>
              <div class="text-h5 font-weight-black">{{ marketColumns.length }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Matriz Grid Tipo Excel -->
    <v-card class="rounded-xl elevation-2 table-card overflow-hidden" outlined>
      <v-card-title class="d-flex align-center py-3 px-4 bg-matrix-header">
        <v-icon color="primary" class="mr-2">mdi-table-pivot</v-icon>
        <span class="text-subtitle-1 font-weight-bold primary--text">
          MATRIZ DE LÍMITES MÁXIMOS DE RESIDUOS (LMR) POR MERCADO
        </span>
        <v-spacer></v-spacer>
        <span class="text-caption grey--text text--darken-1 font-italic">
          Mostrando {{ filteredRows.length }} registros | {{ marketColumns.length }} mercados
        </span>
      </v-card-title>
      <v-divider></v-divider>

      <!-- Tabla con Scroll Horizontal y Columna Fija -->
      <div class="matrix-table-wrapper">
        <v-data-table
          :headers="computedHeaders"
          :items="filteredRows"
          :loading="loading"
          dense
          fixed-header
          height="480px"
          class="matrix-custom-table elevation-0"
          :items-per-page="50"
          :footer-props="{
            'items-per-page-options': [25, 50, 100, -1],
            'items-per-page-text': 'Filas por página:'
          }"
          no-data-text="No se encontraron datos para los filtros seleccionados"
          loading-text="Cargando datos de matriz LMR..."
        >

          <!-- Celda Sustancia (Columna Fija Izquierda) -->
          <template v-slot:item.substanceName="{ item }">
            <span class="substance-label text-truncate d-block" :title="item.substanceName">
              {{ item.substanceName }}
            </span>
          </template>

          <!-- Celda Cultivo -->
          <template v-slot:item.TypeCultiveName="{ item }">
            <span class="grey--text text--darken-2 font-weight-medium">
              {{ item.TypeCultiveName || '-' }}
            </span>
          </template>

          <!-- Celdas Dinámicas de Mercados -->
          <template
            v-for="mkt in marketColumns"
            v-slot:[`item.${mkt.key}`]="{ item }"
          >
            <div
              :key="mkt.key"
              class="text-center font-weight-medium mrl-val"
              :title="getTooltipText(item, mkt)"
            >
              <span v-if="hasValue(item[mkt.key])">
                {{ formatMRLValue(item[mkt.key]) }}
              </span>
              <span v-else class="grey--text text--lighten-2 font-weight-light">-</span>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card>
  </div>
</template>

<script>
import _sMRLsService from "@/services/Technicalassistance/MRLsService.js";
import SSelectCampaignArea from "@/components/Utils/SSelectCampaignArea.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import SSelectSubstances from "@/components/TecnicalAssistence/Substances/SSelectSubstances.vue";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export default {
  name: "MRLsMatrix",
  components: {
    SSelectCampaignArea,
    SSelectDefinition,
    SSelectSubstances,
  },
  data: () => ({
    loading: false,
    search: "",
    debounceTimer: null,
    filters: {
      cmaId: null,
      typeCultive: null,
      TypeSubstances: null,
      asuId: null,
      typeMarket: null,
    },
    rawData: [],
    matrixRows: [],
    filteredRows: [],
    marketColumns: [],
  }),
  computed: {
    computedHeaders() {
      const headers = [
        {
          text: "SUSTANCIA",
          value: "substanceName",
          align: "start",
          sortable: true,
          width: "260px",
          class: "sticky-col header-substance",
        },
      ];

      // Columna de Cultivo
      headers.push({
        text: "CULTIVO",
        value: "TypeCultiveName",
        align: "center",
        sortable: true,
        width: "110px",
      });

      // Columnas dinámicas por cada mercado retornado en el endpoint
      this.marketColumns.forEach((mkt) => {
        headers.push({
          text: `LMR ${mkt.name.toUpperCase()}`,
          value: mkt.key,
          align: "center",
          sortable: true,
          width: "125px",
        });
      });

      return headers;
    },
  },
  created() {
    this.loadData();
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.loadData();
        }, 350);
      },
    },
  },
  methods: {
    loadData() {
      clearTimeout(this.debounceTimer);
      this.loading = true;
      const requestID = this.$fun.getUserID();

      const filterObj = {
        CmaID: this.filters.cmaId && this.filters.cmaId !== 0 ? Number(this.filters.cmaId) : null,
        TypeSubstances: this.filters.TypeSubstances && this.filters.TypeSubstances !== 0 ? Number(this.filters.TypeSubstances) : null,
        AsuID: this.filters.asuId && this.filters.asuId !== 0 ? Number(this.filters.asuId) : null,
        TypeCultive: this.filters.typeCultive && this.filters.typeCultive !== 0 ? Number(this.filters.typeCultive) : null,
        TypeMarket: this.filters.typeMarket && this.filters.typeMarket !== 0 ? Number(this.filters.typeMarket) : null,
      };

      const params = {
        ...filterObj,
        filter: filterObj,
        draw: 1,
        start: 0,
        length: -1,
        order: [{ dir: "asc", column: "" }],
        searchText: this.search || "",
      };

      _sMRLsService
        .MatrizMRLs(params, requestID)
        .then((response) => {
          if (response && response.data) {
            const items = Array.isArray(response.data)
              ? response.data
              : response.data.data || response.data.items || [];
            this.rawData = items;
            this.processMatrixData(items);
          } else {
            this.rawData = [];
            this.matrixRows = [];
            this.filteredRows = [];
            this.marketColumns = [];
          }
        })
        .catch((error) => {
          console.error("Error al cargar datos de MatrizMRLs:", error);
          this.$fun.alert("Error al consultar la matriz de LMR", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    processMatrixData(items) {
      if (!items || items.length === 0) {
        this.matrixRows = [];
        this.filteredRows = [];
        this.marketColumns = [];
        return;
      }

      // 1. Campos fijos del backend que no son columnas de mercados
      const fixedKeys = [
        "DocEntry",
        "DocEntryName",
        "TypeSubstances",
        "TypeSubstancesName",
        "AsuID",
        "ActiveSubstancesName",
        "Description",
        "TypeCultive",
        "TypeCultiveName",
        "TypeHeavyMetals",
        "TypeHeavyMetalsName",
        "HeavyMetalName",
        "CmaID",
        "SecStatus",
        "SecUser",
        "SecDate",
        "totalRecords",
        "RN",
        "id",
        "substanceName",
        "isHeavyMetal",
        "cultiveName",
        "TypeParameter",
      ];

      // 2. Extraer dinámicamente las columnas de mercados a partir de las propiedades del primer registro
      const sample = items[0];
      const marketCols = [];

      Object.keys(sample).forEach((key) => {
        if (!fixedKeys.includes(key)) {
          marketCols.push({
            key: key,
            name: key,
          });
        }
      });

      this.marketColumns = marketCols;

      // 3. Formatear cada fila de la matriz
      this.matrixRows = items.map((item, index) => {
        const isHeavyMetal = item.TypeSubstances === 2 || (!item.AsuID && !!item.TypeHeavyMetals);
        const substanceName = item.substanceName
          || item.DocEntryName
          || item.ActiveSubstancesName
          || item.TypeHeavyMetalsName
          || item.HeavyMetalName
          || item.Description
          || (isHeavyMetal ? `Metal ${item.DocEntry || item.TypeHeavyMetals}` : `Sustancia ${item.DocEntry || item.AsuID || index + 1}`);

        return {
          ...item,
          id: item.DocEntry || item.AsuID || item.TypeHeavyMetals || index + 1,
          substanceName: substanceName ? substanceName.trim() : "Sin Nombre",
          isHeavyMetal,
        };
      });

      this.filteredRows = [...this.matrixRows];
    },

    hasValue(val) {
      return val !== undefined && val !== null && val !== "";
    },

    formatMRLValue(val) {
      if (!this.hasValue(val)) return "-";
      const num = parseFloat(val);
      if (isNaN(num)) return val;
      // Remueve ceros decimales innecesarios (ej. 0.010000 -> 0.01, 100.000000 -> 100)
      return parseFloat(num.toFixed(6));
    },

    getTooltipText(item, mkt) {
      const val = item[mkt.key];
      if (!this.hasValue(val)) return `${item.substanceName} | ${mkt.name}: Sin LMR configurado`;
      return `${item.substanceName} | ${mkt.name}: LMR ${this.formatMRLValue(val)}`;
    },

    async exportToExcel() {
      if (this.filteredRows.length === 0) {
        this.$fun.alert("No hay datos para exportar", "warning");
        return;
      }

      try {
        const workbook = new ExcelJS.Workbook();
        workbook.creator = "SunExpert";
        workbook.created = new Date();

        const worksheet = workbook.addWorksheet("Matriz LMR");
        worksheet.pageSetup.orientation = "landscape";

        // Título del Reporte
        const totalCols = this.marketColumns.length + 2;
        const lastColLetter = this.getColLetter(totalCols);

        worksheet.mergeCells(`A1:${lastColLetter}1`);
        const titleCell = worksheet.getCell("A1");
        titleCell.value = "MATRIZ DE LÍMITES MÁXIMOS DE RESIDUOS (LMR) POR MERCADO";
        titleCell.font = { name: "Segoe UI", size: 13, bold: true, color: { argb: "FFFFFFFF" } };
        titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0D47A1" } };
        titleCell.alignment = { vertical: "middle", horizontal: "center" };
        worksheet.getRow(1).height = 30;

        // Fila de Encabezados
        const headerRowValues = [
          this.recordType === 2 ? "METAL PESADO" : "SUSTANCIA ACTIVA",
          "CULTIVO",
        ];
        this.marketColumns.forEach((mkt) => {
          headerRowValues.push(`LMR ${mkt.name.toUpperCase()}`);
        });

        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.height = 26;

        headerRow.eachCell((cell, colNumber) => {
          cell.font = { name: "Segoe UI", size: 9.5, bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: colNumber === 1 ? "FF1565C0" : "FF1976D2" },
          };
          cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
          cell.border = {
            top: { style: "thin", color: { argb: "FF90CAF9" } },
            left: { style: "thin", color: { argb: "FF90CAF9" } },
            bottom: { style: "medium", color: { argb: "FF0D47A1" } },
            right: { style: "thin", color: { argb: "FF90CAF9" } },
          };
        });

        // Filas de Datos
        this.filteredRows.forEach((row, index) => {
          const rowValues = [row.substanceName, row.TypeCultiveName || ""];

          this.marketColumns.forEach((mkt) => {
            const rawVal = row[mkt.key];
            if (this.hasValue(rawVal)) {
              const numVal = parseFloat(rawVal);
              rowValues.push(isNaN(numVal) ? rawVal : parseFloat(numVal.toFixed(6)));
            } else {
              rowValues.push("");
            }
          });

          const dataRow = worksheet.addRow(rowValues);
          dataRow.height = 20;

          dataRow.eachCell((cell, colNumber) => {
            const isFirstCol = colNumber === 1;
            const isEven = index % 2 === 0;

            cell.font = {
              name: "Segoe UI",
              size: 9,
              bold: isFirstCol,
              color: { argb: "FF212121" },
            };

            const bgArgb = isEven ? "FFFFFFFF" : "FFF9FAFB";

            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgArgb } };
            cell.alignment = {
              vertical: "middle",
              horizontal: isFirstCol ? "left" : "center",
            };
            cell.border = {
              top: { style: "thin", color: { argb: "FFE2E8F0" } },
              left: { style: "thin", color: { argb: "FFE2E8F0" } },
              bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
              right: { style: "thin", color: { argb: "FFE2E8F0" } },
            };
          });
        });

        // Ancho de columnas
        worksheet.columns.forEach((column, colIdx) => {
          if (colIdx === 0) {
            column.width = 36;
          } else if (colIdx === 1) {
            column.width = 16;
          } else {
            column.width = 16;
          }
        });

        // Descarga del archivo
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, `Matriz_LMR_${new Date().toISOString().slice(0, 10)}.xlsx`);
        this.$fun.alert("Matriz exportada exitosamente a Excel", "success");
      } catch (err) {
        console.error("Error exportando a Excel:", err);
        this.$fun.alert("Error al exportar a Excel", "error");
      }
    },

    getColLetter(colIdx) {
      let temp, letter = '';
      while (colIdx > 0) {
        temp = (colIdx - 1) % 26;
        letter = String.fromCharCode(temp + 65) + letter;
        colIdx = (colIdx - temp - 1) / 26;
      }
      return letter || 'A';
    },
  },
};
</script>

<style scoped>
.mrl-matrix-container {
  width: 100%;
}

.filter-panel {
  background: #ffffff;
}

.kpi-card {
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}
.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12) !important;
}

.gradient-blue {
  background: linear-gradient(135deg, #1e88e5 0%, #1565c0 100%) !important;
}

.gradient-teal {
  background: linear-gradient(135deg, #00897b 0%, #00695c 100%) !important;
}

.gradient-indigo {
  background: linear-gradient(135deg, #3949ab 0%, #283593 100%) !important;
}

.bg-matrix-header {
  background-color: #f8fafc;
}

/* Contenedor con Scroll */
.matrix-table-wrapper {
  overflow-x: auto;
  max-width: 100%;
}

/* Estilos de Tabla */
.matrix-custom-table ::v-deep table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}

/* Encabezados Azules estilo Excel Corporativo */
.matrix-custom-table ::v-deep thead th {
  background: #1976d2 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  font-size: 0.78rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  height: 40px !important;
  border-right: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-bottom: 2px solid #0d47a1 !important;
  padding: 4px 8px !important;
}

.matrix-custom-table ::v-deep thead th.header-substance {
  background: #1565c0 !important;
  min-width: 250px;
  width: 250px;
  border-right: 2px solid #0d47a1 !important;
}

/* Columna Fija Izquierda (Sticky Column) */
.matrix-custom-table ::v-deep thead th.sticky-col {
  position: sticky !important;
  left: 0 !important;
  top: 0 !important;
  z-index: 5 !important;
}

.matrix-custom-table ::v-deep tbody td:first-child {
  position: sticky !important;
  left: 0 !important;
  background-color: #ffffff !important;
  z-index: 2 !important;
  border-right: 2px solid #e2e8f0 !important;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.04);
}

.matrix-custom-table ::v-deep tbody tr:hover td:first-child {
  background-color: #f1f5f9 !important;
}

/* Celdas de datos */
.matrix-custom-table ::v-deep tbody td {
  border-right: 1px solid #edf2f7;
  border-bottom: 1px solid #edf2f7;
  font-size: 0.84rem;
  padding: 4px 8px !important;
  height: 36px !important;
}

.matrix-custom-table ::v-deep tbody tr:hover td {
  background-color: #f8fafc;
}

.substance-label {
  font-weight: 600;
  color: #1e293b;
}

.mrl-val {
  font-variant-numeric: tabular-nums;
  color: #1e293b;
}
</style>
